import { CFormCheck, CSpinner } from "@coreui/react";
import { FormEvent } from "react";
import { useMutation, useQueryClient } from "react-query";
import BoxWrapper from "../../../components/BoxWrapper";
import apiService from "../../../service/apiService";
import { general } from "../../../locales/general";
import { useCookie } from "../../../hooks/useCookie";
import { useToast } from "../../../hooks/useToast";
import RestrictedRoute from "../../auth/RestrictedRoute";

interface NewCityData {
  label: string;
  latitude: number;
  longitude: number;
  isActive: boolean;
  isEnabled: boolean;
}

interface Props {
  onClose: () => void;
  onAccept?: () => void;
}

export default function NewCity({ onClose, onAccept }: Props) {
  const { toastSuccess, toastError } = useToast();
  const { token } = useCookie("vToken");
  const queryClient = useQueryClient();

  const createNewCityMutation = useMutation(async (data: NewCityData) => {
    return await apiService.MakePostRequest("cities", data, token);
  });

  const createCityHandler = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const label = formData.get("label") as string;
    const latitude = parseFloat(formData.get("lat") as string);
    const longitude = parseFloat(formData.get("long") as string);

    const isActive = formData.get("isActive") !== null;
    const isEnabled = formData.get("isEnabled") !== null;

    // Validate coordinates
    if (isNaN(latitude) || isNaN(longitude)) {
      toastError("Latitude et Longitude doivent être des nombres valides");
      return;
    }

    const data: NewCityData = {
      label,
      latitude,
      longitude,
      isActive,
      isEnabled,
    };

    createNewCityMutation.mutate(data, {
      onSuccess() {
        toastSuccess(general.fr.message.cityCreated);
        queryClient.invalidateQueries("getAllCities");

        if (onAccept) {
          onAccept();
        }
        onClose();
      },
      onError(error: any) {
        console.log(error);
        toastError(general.fr.message.operationFailed);
      },
    });
  };

  return (
    <RestrictedRoute>
      <div className="mt-4">
        <BoxWrapper>
          <section className="p-4">
            <div className="border-bottom border-success mb-3">
              <h4>Ajouter une ville</h4>
            </div>
            <form className="row form" onSubmit={createCityHandler}>
              <div className="col-12 mb-3">
                <label htmlFor="label" className="d-block">
                  Ville {" "}
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="custom-input"
                  placeholder="Ville"
                  id="label"
                  name="label"
                  required
                />
              </div>

              <div className="col-12 mb-3">
                <label htmlFor="long" className="d-block">
                  Longitude {" "}
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  step="any"
                  className="custom-input"
                  placeholder="Ex: -7.5833"
                  id="long"
                  name="long"
                  required
                />
              </div>

              <div className="col-12 mb-3">
                <label htmlFor="lat" className="d-block">
                  Latitude {" "}
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  step="any"
                  className="custom-input"
                  placeholder="Ex: 33.5333"
                  id="lat"
                  name="lat"
                  required
                />
              </div>

              <div className="col-12 mb-3">
                <label htmlFor="isActive" className="d-block">
                  Active
                </label>
                <CFormCheck
                  type="checkbox"
                  className="custom-input"
                  id="isActive"
                  name="isActive"
                  defaultChecked={false}
                />
              </div>

              <div className="col-12 mb-3">
                <label htmlFor="isEnabled" className="d-block">
                  Enabled
                </label>
                <CFormCheck
                  type="checkbox"
                  className="custom-input"
                  id="isEnabled"
                  name="isEnabled"
                  defaultChecked={false}
                />
              </div>

              <section className="d-flex justify-content-start buttons gap-4 mt-3">
                <button
                  className="btn btn-danger text-white shadow-primary w-20 px-4 py-2"
                  type="button"
                  onClick={onClose}
                >
                  Annuler
                </button>
                <button
                  className="btn btn-success shadow-secondary w-20 text-white"
                  type="submit"
                  disabled={createNewCityMutation.isLoading}
                >
                  {createNewCityMutation.isLoading ? (
                    <div className="text-center">
                      <CSpinner size="sm" />
                    </div>
                  ) : (
                    "Enregistrer"
                  )}
                </button>
              </section>
            </form>
          </section>
        </BoxWrapper>
      </div>
    </RestrictedRoute>
  );
}
